import { useEffect } from 'react';
import { type Params, useHref, useMatches } from 'react-router-dom';
import { OG_ANALYTICS_SOURCE_APP, useOgAnalytics } from './ogAnalyticsClient';

const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * `/springfield/mds/42` with `{ entitySubdomain: 'springfield', reportId: '42' }` →
 * `/:entitySubdomain/mds/:reportId`. `@opengov/analytics` expects page names to be route
 * patterns: a raw URL makes one row per record and can never be charted.
 */
export const toRoutePattern = (pathname: string, params: Params): string => {
  const paramNameByValue = new Map<string, string>();
  Object.entries(params).forEach(([paramName, paramValue]) => {
    if (paramName !== '*' && paramValue) {
      paramNameByValue.set(paramValue, paramName);
    }
  });
  return pathname
    .split('/')
    .map((segment) => {
      const paramName = paramNameByValue.get(safeDecode(segment));
      return paramName ? `:${paramName}` : segment;
    })
    .join('/');
};

/**
 * Prefixes the router basename, which react-router strips from `useMatches()`, so the page name is
 * the full pattern from the URL root (`/reporting/:entitySubdomain`).
 */
export const withBasename = (basename: string, pattern: string): string => {
  const base = basename.endsWith('/') ? basename.slice(0, -1) : basename;
  if (pattern === '/') {
    return base || '/';
  }
  return `${base}${pattern}`;
};

/**
 * Sends a page view, named by route pattern, whenever the pattern changes — not when only a param
 * value does.
 *
 * Standalone only: mounted in PSP the shell tracks URL changes itself, so a second page view from
 * here would double-count. Nothing is sent while the client is the no-op one (flag off or
 * non-analytics build).
 */
export function useOgPageTracking(): void {
  const analytics = useOgAnalytics();
  const matches = useMatches();
  const basename = useHref('/');
  const deepest = matches.length > 0 ? matches[matches.length - 1] : undefined;
  const pattern = deepest ? withBasename(basename, toRoutePattern(deepest.pathname, deepest.params)) : undefined;

  useEffect(() => {
    if (analytics.mode !== 'standalone' || !pattern) {
      return;
    }
    analytics.page(pattern, { source_app: OG_ANALYTICS_SOURCE_APP });
  }, [analytics, pattern]);
}
